import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { en } from "@/lib/i18n/en";
import { he } from "@/lib/i18n/he";
import { ru } from "@/lib/i18n/ru";
import { LOCALE_DIRECTIONS, type Locale, type LocaleSetting, type Translations } from "@/lib/i18n/types";

const STORAGE_KEY = "loreweaver-locale";
const LOCALE_QUERY_PARAM = "lang";

const LOCALE_MAP: Record<Locale, Translations> = { en, he, ru };

function detectBrowserLocale(): Locale {
  const lang = navigator.language?.toLowerCase() || "";
  if (lang.startsWith("he")) return "he";
  if (lang.startsWith("ru")) return "ru";
  return "en";
}

function resolveLocale(setting: LocaleSetting): Locale {
  if (setting === "auto") return detectBrowserLocale();
  return setting;
}

/**
 * Read the stored locale preference and resolve "auto" to a real BCP-47 locale.
 * Safe to call outside React — reads localStorage and navigator.language directly.
 */
export function getResolvedLocale(): Locale {
  return resolveLocale(getStoredSetting());
}

interface LocaleContextValue {
  locale: Locale;
  localeSetting: LocaleSetting;
  setLocale: (setting: LocaleSetting) => void;
  t: Translations;
  dir: "ltr" | "rtl";
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: "en",
  localeSetting: "auto",
  setLocale: () => {},
  t: en,
  dir: "ltr",
});

function getStoredSetting(): LocaleSetting {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "auto") return "auto";
  if (stored && stored in LOCALE_MAP) return stored as Locale;
  return "auto";
}

function getSettingFromSearch(search: string): LocaleSetting | null {
  const params = new URLSearchParams(search);
  const value = params.get(LOCALE_QUERY_PARAM);
  if (value === "auto") return "auto";
  if (value && value in LOCALE_MAP) return value as Locale;
  return null;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [setting, setSettingState] = useState<LocaleSetting>(() => getSettingFromSearch(window.location.search) ?? getStoredSetting());
  const resolved = resolveLocale(setting);

  // Ref to track navigations we initiated, preventing feedback loops
  const lastWrittenSearch = useRef<string | null>(null);

  const setLocale = useCallback((s: LocaleSetting) => {
    setSettingState(s);
    localStorage.setItem(STORAGE_KEY, s);
  }, []);

  // URL → state: only when an external navigation changes the lang param
  useEffect(() => {
    // Skip if this navigation was caused by our own state→URL sync
    if (lastWrittenSearch.current === location.search) return;

    const urlSetting = getSettingFromSearch(location.search);
    if (urlSetting && urlSetting !== setting) {
      setSettingState(urlSetting);
      localStorage.setItem(STORAGE_KEY, urlSetting);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // State → URL: ensure the lang param matches the current setting.
  // "auto" is treated as "no param" — we never write ?lang=auto to the URL
  // so shareable links stay clean and users who never picked a locale don't
  // get ?lang=auto appended to every page visit (i18n-audit-2026-04, Medium).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentUrlLang = params.get(LOCALE_QUERY_PARAM);

    let newSearch: string;
    if (setting === "auto") {
      // Remove lang param if it's there
      if (!currentUrlLang) return;
      params.delete(LOCALE_QUERY_PARAM);
      newSearch = params.size > 0 ? `?${params.toString()}` : "";
    } else {
      // Keep param in sync with the chosen locale
      if (currentUrlLang === setting) return;
      params.set(LOCALE_QUERY_PARAM, setting);
      newSearch = `?${params.toString()}`;
    }

    // Mark this navigation as ours so the URL→state effect ignores it
    lastWrittenSearch.current = newSearch;

    navigate(
      {
        pathname: location.pathname,
        search: newSearch,
        hash: location.hash,
      },
      { replace: true },
    );
  }, [setting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply dir attribute to <html>
  useEffect(() => {
    const dir = LOCALE_DIRECTIONS[resolved];
    document.documentElement.dir = dir;
    document.documentElement.lang = resolved;
  }, [resolved]);

  const value: LocaleContextValue = {
    locale: resolved,
    localeSetting: setting,
    setLocale,
    t: LOCALE_MAP[resolved],
    dir: LOCALE_DIRECTIONS[resolved],
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}
